import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { dataManagerApi } from '@services/dataManagerApi'
import './DataBrowser.css'

interface BrowserConfig {
  domain: string
  path: string
  label: string
  columns: string[]
  readOnlyColumns?: string[]
}

interface DataBrowserProps {
  companyId: number
  config: BrowserConfig
}

type SortOrder = 'asc' | 'desc'

/* ------------------------------------------------------------------ */
/*  Value formatting helpers                                          */
/* ------------------------------------------------------------------ */

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T|\s)/

function formatValue(value: unknown, key: string): string {
  if (value === null || value === undefined) return '-'

  if (typeof value === 'boolean') return value ? 'Yes' : 'No'

  if (typeof value === 'string') {
    // Boolean-ish strings
    if (value === 'true' || value === 'false') return value === 'true' ? 'Yes' : 'No'
    // Date detection
    if (ISO_DATE_RE.test(value)) return value.slice(0, 10)
  }

  if (typeof value === 'number') {
    const lk = key.toLowerCase()
    if (lk === 'id' || lk.endsWith('_id')) {
      return String(value)
    }
    if (lk.includes('amount') || lk.includes('price') || lk.includes('cost') || lk.includes('balance') || lk.includes('revenue')) {
      return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
    return value.toLocaleString()
  }

  return String(value)
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

function DataBrowser({ companyId, config }: DataBrowserProps) {
  const queryClient = useQueryClient()

  // Pagination & sort state
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [sortBy, setSortBy] = useState<string | null>(null)
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')

  // Add-row form state
  const [showAddForm, setShowAddForm] = useState(false)
  const [addFormData, setAddFormData] = useState<Record<string, string>>({})

  // Edit-row state
  const [editRowId, setEditRowId] = useState<number | null>(null)
  const [editFormData, setEditFormData] = useState<Record<string, string>>({})

  // Delete confirm state
  const [deleteRowId, setDeleteRowId] = useState<number | null>(null)

  const _ro = new Set(['id', ...(config.readOnlyColumns ?? [])])
  const editableColumns = config.columns.filter((c) => !_ro.has(c))

  const queryKey = ['data', config.domain, config.path, companyId, page, pageSize, sortBy, sortOrder]

  // ---- Fetch records ----
  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () =>
      dataManagerApi.listRecords(config.domain, config.path, companyId, {
        page,
        page_size: pageSize,
        ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
      }),
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  // ---- Mutations ----
  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['data', config.domain, config.path, companyId] }),
    [queryClient, config.domain, config.path, companyId],
  )

  const createMutation = useMutation({
    mutationFn: (newData: Record<string, unknown>) =>
      dataManagerApi.createRecord(config.domain, config.path, companyId, newData),
    onSuccess: () => {
      invalidate()
      setShowAddForm(false)
      setAddFormData({})
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      dataManagerApi.updateRecord(config.domain, config.path, companyId, id, payload),
    onSuccess: () => {
      invalidate()
      setEditRowId(null)
      setEditFormData({})
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => dataManagerApi.deleteRecord(config.domain, config.path, companyId, id),
    onSuccess: () => {
      invalidate()
      setDeleteRowId(null)
    },
  })

  // ---- Handlers ----
  const handleSort = (col: string) => {
    if (sortBy === col) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(col)
      setSortOrder('asc')
    }
    setPage(1)
  }

  const handleAddSave = () => {
    const payload: Record<string, unknown> = { ...addFormData }
    createMutation.mutate(payload)
  }

  const startEdit = (row: Record<string, unknown>) => {
    const id = row.id as number
    setEditRowId(id)
    const formValues: Record<string, string> = {}
    for (const col of editableColumns) {
      const v = row[col]
      formValues[col] = v === null || v === undefined ? '' : String(v)
    }
    setEditFormData(formValues)
  }

  const handleEditSave = () => {
    if (editRowId === null) return
    const payload: Record<string, unknown> = { ...editFormData }
    updateMutation.mutate({ id: editRowId, payload })
  }

  // ---- Render ----
  return (
    <div className="db-container">
      {/* Toolbar */}
      <div className="db-toolbar">
        <h2>{config.label}</h2>
        <button className="db-add-btn" onClick={() => { setShowAddForm(true); setAddFormData({}) }}>
          + Add Row
        </button>
      </div>

      {/* Add form */}
      {showAddForm && (
        <div className="db-inline-form">
          {editableColumns.map((col) => (
            <input
              key={col}
              placeholder={col}
              value={addFormData[col] ?? ''}
              onChange={(e) => setAddFormData((prev) => ({ ...prev, [col]: e.target.value }))}
            />
          ))}
          <div className="db-form-actions">
            <button className="db-save-btn" onClick={handleAddSave} disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Saving...' : 'Save'}
            </button>
            <button className="db-cancel-btn" onClick={() => setShowAddForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="db-table-wrapper">
        {isLoading ? (
          <div className="db-loading">Loading records...</div>
        ) : isError ? (
          <div className="db-error">Failed to load records. Please try again.</div>
        ) : items.length === 0 ? (
          <div className="db-empty">No records found.</div>
        ) : (
          <table className="db-table">
            <thead>
              <tr>
                {config.columns.map((col) => (
                  <th
                    key={col}
                    className={sortBy === col ? 'db-th-active' : ''}
                    onClick={() => handleSort(col)}
                  >
                    {col.replace(/_/g, ' ')}
                    {sortBy === col && (
                      <span className="db-sort-indicator">{sortOrder === 'asc' ? '\u25B2' : '\u25BC'}</span>
                    )}
                  </th>
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const rowId = row.id as number
                const isEditing = editRowId === rowId

                return (
                  <tr key={rowId} className={isEditing ? 'db-edit-row' : ''}>
                    {config.columns.map((col) => (
                      <td key={col}>
                        {isEditing && col !== 'id' ? (
                          <input
                            className="db-edit-input"
                            value={editFormData[col] ?? ''}
                            onChange={(e) =>
                              setEditFormData((prev) => ({ ...prev, [col]: e.target.value }))
                            }
                          />
                        ) : (
                          formatValue(row[col], col)
                        )}
                      </td>
                    ))}
                    <td>
                      <div className="db-row-actions">
                        {isEditing ? (
                          <>
                            <button className="db-action-btn" onClick={handleEditSave} disabled={updateMutation.isPending}>
                              {updateMutation.isPending ? '...' : 'Save'}
                            </button>
                            <button className="db-action-btn" onClick={() => setEditRowId(null)}>Cancel</button>
                          </>
                        ) : (
                          <>
                            <button className="db-action-btn" onClick={() => startEdit(row)}>Edit</button>
                            <button className="db-action-btn db-delete-btn" onClick={() => setDeleteRowId(rowId)}>Delete</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && !isError && items.length > 0 && (
        <div className="db-pagination">
          <span>
            {total} record{total !== 1 ? 's' : ''} total
          </span>
          <div className="db-pagination-controls">
            <button className="db-page-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button className="db-page-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Next
            </button>
          </div>
          <div className="db-page-size">
            <label>
              Show{' '}
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>
          </div>
        </div>
      )}

      {/* Delete confirm dialog */}
      {deleteRowId !== null && (
        <div className="db-confirm-overlay" onClick={() => setDeleteRowId(null)}>
          <div className="db-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <p>Are you sure you want to delete this record?</p>
            <div className="db-confirm-actions">
              <button className="db-confirm-cancel" onClick={() => setDeleteRowId(null)}>Cancel</button>
              <button
                className="db-confirm-delete"
                onClick={() => deleteMutation.mutate(deleteRowId)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DataBrowser
